import * as React from "react";
import { useEffect, useState } from "react";
import { CALL_STATUS_COLORS, CALL_STATUS_LABELS, CallStatus, CAMPAIGN_STATUS_COLORS } from "@/constants";
import {
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	Divider,
	Fade,
	Grid,
	Link,
	Stack,
	Tab,
	Tabs,
	Tooltip,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { MegaphoneIcon, PencilSimpleIcon, TargetIcon } from "@phosphor-icons/react";
import { PlayIcon } from "@phosphor-icons/react/dist/icons/Play";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { ClockIcon } from "@phosphor-icons/react/dist/ssr/Clock";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { PauseIcon } from "@phosphor-icons/react/dist/ssr/Pause";
import { PhoneCallIcon } from "@phosphor-icons/react/dist/ssr/PhoneCall";
import { PhoneIcon } from "@phosphor-icons/react/dist/ssr/Phone";
import { UserCircleIcon } from "@phosphor-icons/react/dist/ssr/UserCircle";
import { UsersIcon } from "@phosphor-icons/react/dist/ssr/Users";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { dayjs } from "@/lib/dayjs";
import { useCallLogsStats } from "@/hooks/api/use-call-logs";
import { campaignsKeys, useCampaign, useCampaignInfo, useToggleCampaign } from "@/hooks/api/use-campaigns";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { CallLogs } from "@/components/call-logs";
import { useAnimation } from "@/components/core/animation-provider";
import { CallDispositionsCard } from "@/components/core/call-dispositions-card/call-dispositions-card";
import { InfoCard } from "@/components/core/info-card/info-card";
import { RouterLink } from "@/components/core/link";
import { toast } from "@/components/core/toaster";
import { CustomSkeleton } from "@/components/customSkeleton/custom-skeleton";
import { FilterType } from "@/components/customTable/types";
import { StatisticCard } from "@/components/statisticCard/statistic-card";

import { CampaignTrendCard } from "./campaign-trend-card";
import { LeadList } from "./lead-list";
import { ListPenetrationCard } from "./list-penetration-card";

const formatMinutes = (seconds: number) => Math.round((seconds / 60) * 100) / 100;

const TABS = [
	{ label: "Call List", value: "calls" },
	{ label: "Campaign Contacts", value: "contacts" },
];

const animationDelay = 100;

export function CampaignView(): React.JSX.Element {
	const { isAnimated } = useAnimation();
	const [searchParams] = useSearchParams();

	const [isDispositionsCardVisible, setIsDispositionsCardVisible] = useState(isAnimated);
	const [isInfoCardVisible, setIsInfoCardVisible] = useState(isAnimated);
	const [activeTab, setActiveTab] = useState(searchParams.get("tab") || (TABS[0].value as string));

	const { campaignId } = useParams();

	const navigate = useNavigate();

	const { data: campaignData, isLoading: isCampaignLoading } = useCampaign(campaignId || "");
	const { data: stats, isLoading: isCallLogsStatsLoading } = useCallLogsStats(campaignId || "");
	const { data: campaignInfoData, isLoading: isCampaignInfoLoading } = useCampaignInfo(campaignId || "");
	const toggleCampaignMutation = useToggleCampaign();

	const isLoading = isCampaignLoading || isCampaignInfoLoading;

	React.useEffect(() => {
		setTimeout(
			() => {
				setIsInfoCardVisible(true);
			},
			(animationDelay * (statisticCards.length + 1)) / 2
		);

		setTimeout(() => {
			setIsDispositionsCardVisible(true);
		}, animationDelay * statisticCards.length);
	}, []);

	useEffect(() => {
		setActiveTab(searchParams.get("tab") || (TABS[0].value as string));
	}, [searchParams]);

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		navigate(`/campaigns/${campaignId || ""}/view?tab=${newValue}`);
	};

	const handleToggleCampaign = async () => {
		if (!campaignData) return;
		try {
			await toggleCampaignMutation.mutateAsync({
				id: campaignData.id,
				enabled: !campaignData.enabled,
			});
		} catch {
			toast.error("Failed to toggle campaign");
		}
	};

	const isOutbound = (campaignInfoData?.listHealth?.contactsInList ?? 0) > 0;

	const statisticCards = React.useMemo(() => {
		const listHealth = campaignInfoData?.listHealth;
		const rates = campaignInfoData?.rates;
		const totals = campaignInfoData?.totals;
		const leads = campaignInfoData?.leads;

		const statusCard = {
			title: "Campaign Status",
			icon: <MegaphoneIcon color="var(--mui-palette-primary-main)" width={20} height={20} />,
			additonalInfo: (
				<Chip
					label={campaignInfoData?.status}
					size="small"
					color={CAMPAIGN_STATUS_COLORS[campaignInfoData?.status || "inactive"]}
					variant="outlined"
				/>
			),
		};

		if (isOutbound) {
			// List health row (outbound)
			return [
				statusCard,
				{
					title: "Total Contacts",
					value: listHealth?.contactsInList ?? 0,
					icon: <UsersIcon color="var(--mui-palette-info-main)" width={20} height={20} />,
					additonalInfo: "Contacts in list",
				},
				{
					title: "Unique Dialed",
					value: listHealth?.uniqueContactsDialed ?? 0,
					icon: <PhoneCallIcon color="var(--mui-palette-primary-main)" width={20} height={20} />,
					additonalInfo: `${listHealth?.undialedContacts ?? 0} undialed remaining`,
				},
				{
					title: "% List Dialed",
					value: `${listHealth?.listPenetrationRate ?? 0}%`,
					icon: <TargetIcon color="var(--mui-palette-primary-main)" width={20} height={20} />,
					additonalInfo: "List penetration",
				},
				{
					title: "Total Dials",
					value: totals?.totalOutboundDials ?? stats?.callsCount ?? 0,
					icon: <PhoneIcon color="var(--mui-palette-info-main)" width={20} height={20} />,
					additonalInfo: "Outbound calls incl. retries",
				},
				{
					title: "Connection Rate",
					value: `${rates?.connectionRate ?? 0}%`,
					icon: <PhoneCallIcon color="var(--mui-palette-info-main)" width={20} height={20} />,
					additonalInfo: `${totals?.connectedCalls ?? 0} connected ÷ dials`,
				},
				{
					title: "Conversion Rate",
					value: `${rates?.conversionRate ?? 0}%`,
					icon: <TargetIcon color="var(--mui-palette-success-main)" width={20} height={20} />,
					additonalInfo: `${totals?.conversionsCount ?? 0} conversions ÷ connected`,
				},
				{
					title: "AI Minutes",
					value: formatMinutes(campaignInfoData?.duration ?? 0),
					icon: <ClockIcon color="var(--mui-palette-warning-main)" width={20} height={20} />,
					additonalInfo: "Minutes of AI conversation",
				},
			];
		}

		// Inbound service-first scorecard
		return [
			statusCard,
			{
				title: "Calls Received",
				value: stats?.callsCount ?? 0,
				icon: <PhoneIcon color="var(--mui-palette-info-main)" width={20} height={20} />,
				additonalInfo: "Inbound calls",
			},
			{
				title: "Unique Callers",
				value: listHealth?.uniqueContactsConnected ?? 0,
				icon: <UserCircleIcon color="var(--mui-palette-primary-main)" width={20} height={20} />,
				additonalInfo: "Distinct contacts",
			},
			{
				title: "Resolution Rate",
				value: `${rates?.conversionRate ?? 0}%`,
				icon: <CheckCircleIcon color="var(--mui-palette-success-main)" width={20} height={20} />,
				additonalInfo: `${totals?.conversionsCount ?? 0} resolved`,
			},
			{
				title: "Lead Completion",
				value: `${rates?.leadCompletionRate ?? 0}%`,
				icon: <CheckCircleIcon color="var(--mui-palette-success-main)" width={20} height={20} />,
				additonalInfo: `${leads?.done ?? 0} / ${leads?.total ?? 0} leads`,
			},
			{
				title: "AI Minutes",
				value: formatMinutes(campaignInfoData?.duration ?? 0),
				icon: <ClockIcon color="var(--mui-palette-warning-main)" width={20} height={20} />,
				additonalInfo: "Minutes of AI conversation",
			},
		];
	}, [campaignInfoData, stats, isOutbound]);

	const campaignInfo = React.useMemo(() => {
		return {
			title: "Campaign Information",
			subtitle: "Basic campaign details and configuration",
			fields: [
				{
					label: "Agent",
					value: campaignData?.agent?.name ? (
						<Tooltip title="Go to agent" arrow placement="right">
							<Box
								tabIndex={0}
								onClick={() => navigate(`/agents/${campaignData?.agent?.id}`)}
								sx={{
									textDecoration: "none",
									color: "inherit",
									cursor: "pointer",
									width: "max-content",
									"&:hover": { textDecoration: "underline" },
								}}
							>
								{campaignData?.agent?.name}
							</Box>
						</Tooltip>
					) : (
						"N/A"
					),
					type: "text" as const,
				},
				{ label: "Start Date", value: campaignData?.startAt || "N/A", type: "date" as const },
				{ label: "End Date", value: campaignData?.endAt || "N/A", type: "date" as const },
				{
					label: "Created",
					value: campaignData?.createdAt ? dayjs(campaignData.createdAt).format("YYYY-MM-DD, HH:mm:ss") : "N/A",
					type: "date" as const,
				},
				...(campaignData?.group
					? [{ label: "Lead Group", value: campaignData?.group?.name, type: "tags" as const }]
					: []),
        { label: "Timezone", value: campaignData?.timezone || "N/A", type: "text" as const },
			],
		};
	}, [campaignData]);

	const callDispositions = React.useMemo(() => {
		return {
			title: "Call Dispositions",
			subtitle: "★ counts as conversion",
			dispositions:
				campaignInfoData?.dispositions?.map((disposition) => ({
					label: disposition.countsAsConversion ? `★ ${disposition.label}` : disposition.label,
					count: disposition.count,
					color: (disposition.countsAsConversion ? "success" : disposition.color) as
						| "success"
						| "error"
						| "warning"
						| "info",
				})) || [],
		};
	}, [campaignInfoData]);

	const callStatusesStatistic = React.useMemo(() => {
		return {
			title: "Call Statuses",
			subtitle: "Breakdown of call statuses",
			dispositions:
				stats?.callStatuses?.map((status) => ({
					label: CALL_STATUS_LABELS[status.status as CallStatus],
					count: status.count,
					color: CALL_STATUS_COLORS[status.status as CallStatus] || "primary",
				})) || [],
		};
	}, [stats]);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={"column"} spacing={3}>
						<Breadcrumbs>
							<Link
								color="text.primary"
								component={RouterLink}
								href={"/campaigns"}
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
								Campaigns
							</Link>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								/
							</Typography>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								{isCampaignLoading ? <CustomSkeleton width={100} height={20} /> : campaignData?.title}
							</Typography>
						</Breadcrumbs>

						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={3}
							sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
						>
							<Box sx={{ flex: "1 1 auto" }}>
								<Box>
									<Typography variant="h4">Campaign Details</Typography>
								</Box>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
								{/* <Button startIcon={<DownloadIcon />} variant="outlined" onClick={() => {}}>
									Export Calls
								</Button> */}

								<Button
									startIcon={
										isLoading || toggleCampaignMutation.isPending ? null : campaignData?.enabled ? (
											<PauseIcon />
										) : (
											<PlayIcon />
										)
									}
									variant="contained"
									sx={{ width: "169px", minHeight: "40px" }}
									onClick={handleToggleCampaign}
									disabled={isLoading || toggleCampaignMutation.isPending}
								>
									{isLoading || toggleCampaignMutation.isPending ? (
										<CircularProgress size={20} sx={{ color: "white" }} />
									) : campaignData?.enabled ? (
										"Pause Campaign"
									) : (
										"Start Campaign"
									)}
								</Button>
								<Button
									startIcon={<PencilSimpleIcon />}
									variant="outlined"
									onClick={() => {
										navigate(`/campaigns/${campaignId}`);
									}}
								>
									Edit Campaign
								</Button>
							</Box>
						</Stack>
					</Stack>

					<Grid container spacing={3}>
						{statisticCards.map((card, index) => (
							<Grid
								key={index}
								size={{
									md: 3,
									sm: 6,
									xs: 12,
								}}
							>
								<StatisticCard
									{...card}
									isLoading={isLoading || isCallLogsStatsLoading}
									animationDelay={index * animationDelay}
									timeout={animationDelay * statisticCards.length}
									isLast={index === statisticCards.length - 1}
								/>
							</Grid>
						))}
					</Grid>

					{isOutbound && (
						<Fade in={isInfoCardVisible} timeout={isAnimated ? 0 : animationDelay * statisticCards.length}>
							<Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>

							<ListPenetrationCard listHealth={campaignInfoData?.listHealth} isLoading={isLoading} />
							</Grid>
						</Fade>
					)}

					{campaignId && (
						<Grid container spacing={3}>
							<Fade in={isInfoCardVisible} timeout={isAnimated ? 0 : animationDelay * statisticCards.length}>
							<Grid size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
								<CampaignTrendCard
									campaignId={campaignId}
									metric="conversions"
									title="Conversions"
									subtitle="Conversion-flagged dispositions"
								/>
							</Grid>
							</Fade>
							{isOutbound && (
								<Fade in={isInfoCardVisible} timeout={isAnimated ? 0 : animationDelay * statisticCards.length}>
								<Grid size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
									<CampaignTrendCard
										campaignId={campaignId}
										metric="penetration"
										title="List penetration"
										subtitle="Cumulative % of list dialed"
										valueSuffix="%"
									/>
								</Grid>
								</Fade>
							)}
						</Grid>
					)}

					<Grid container spacing={3}>
						<Fade in={isInfoCardVisible} timeout={isAnimated ? 0 : animationDelay * statisticCards.length}>
							<Grid size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
								<InfoCard
									title={campaignInfo.title}
									subtitle={campaignInfo.subtitle}
									fields={campaignInfo.fields}
									isLoading={isLoading}
								/>
							</Grid>
						</Fade>
						<Grid size={{ lg: 6, md: 12, sm: 12, xs: 12 }} container spacing={3} style={{ height: "max-content" }}>
							<Fade in={isDispositionsCardVisible} timeout={isAnimated ? 0 : animationDelay * statisticCards.length}>
								<Grid size={{ lg: 6, md: 6, sm: 6, xs: 12 }}>
									<CallDispositionsCard
										title={callStatusesStatistic.title}
										subtitle={callStatusesStatistic.subtitle}
										dispositions={callStatusesStatistic.dispositions}
										isLoading={isLoading}
									/>
								</Grid>
							</Fade>
							<Fade in={isDispositionsCardVisible} timeout={isAnimated ? 0 : animationDelay * statisticCards.length}>
								<Grid size={{ lg: 6, md: 6, sm: 6, xs: 12 }}>
									<CallDispositionsCard
										title={callDispositions.title}
										subtitle={callDispositions.subtitle}
										dispositions={callDispositions.dispositions}
										isLoading={isLoading}
									/>
								</Grid>
							</Fade>
						</Grid>
					</Grid>

					<Card sx={{ position: "relative" }}>
						<Tabs onChange={handleTabChange} sx={{ px: 3 }} value={activeTab} variant="scrollable">
							{TABS.map((tab) => (
								<Tab
									iconPosition="end"
									key={tab.value}
									label={tab.label}
									sx={{ minHeight: "auto" }}
									tabIndex={0}
									value={tab.value}
								/>
							))}
						</Tabs>

						<Divider />

						{activeTab === "contacts" && <LeadList />}
						{activeTab === "calls" && (
							<CallLogs
								queryKey={campaignsKeys.calls(campaignId!)}
								endpoint={`/calls?campaignId=${campaignId}`}
								filtersOptions={[
									{
										name: "status",
										label: "Status",
										value: [],
										type: FilterType.SELECT,
										options: Object.values(CALL_STATUS_LABELS).map((label) => ({
											label,
											value: CallStatus[label.replace(" ", "")],
										})),
									},
								]}
								hideCampaign
								hideAgents
								showExportButton
								campaign={campaignData}
							/>
						)}
					</Card>
				</Stack>
			</Box>
		</React.Fragment>
	);
}
